import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Typography as MuiTypography
} from '@mui/material';
import { LockOutlined, PersonAddOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const AuthScreen: React.FC = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [secretCode, setSecretCode] = useState('');
  // Account type. This is a UI affordance only: the backend derives the real
  // role from WHICH registration code was entered (ADMIN_REGISTER_SECRET vs
  // COUNTER_REGISTER_SECRET) and rejects a mismatch. A picker that the shared
  // secret alone validated would let a 門市 hire mint themselves an admin.
  const [registerRole, setRegisterRole] = useState<'admin' | 'counter'>('admin');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await login(loginEmail, loginPassword);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (registerPassword !== registerConfirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate secret code
    if (!secretCode) {
      setError('Secret code is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await register(registerName, registerEmail, registerPassword, secretCode, registerRole);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          width: '100%', 
          maxWidth: 500,
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Orders Management System
          </Typography>
        </Box>
        
        <Tabs value={tabValue} onChange={handleTabChange} centered variant="fullWidth">
          <Tab icon={<LockOutlined />} label="Login" id="auth-tab-0" aria-controls="auth-tabpanel-0" />
          <Tab icon={<PersonAddOutlined />} label="Register" id="auth-tab-1" aria-controls="auth-tabpanel-1" />
        </Tabs>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleLogin}>
            <TextField
              type="email"
              label="Email"
              fullWidth
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              margin="normal"
              required
              autoFocus={tabValue === 0}
            />
            <TextField
              type="password"
              label="Password"
              fullWidth
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              margin="normal"
              required
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Login'}
            </Button>
          </form>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <form onSubmit={handleRegister}>
            <TextField
              label="Full Name"
              fullWidth
              value={registerName}
              onChange={(e) => setRegisterName(e.target.value)}
              margin="normal"
              required
              autoFocus={tabValue === 1}
            />
            <TextField
              type="email"
              label="Email"
              fullWidth
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              type="password"
              label="Password"
              fullWidth
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              type="password"
              label="Confirm Password"
              fullWidth
              value={registerConfirmPassword}
              onChange={(e) => setRegisterConfirmPassword(e.target.value)}
              margin="normal"
              required
              error={registerPassword !== registerConfirmPassword}
              helperText={registerPassword !== registerConfirmPassword ? "Passwords don't match" : ""}
            />
            <Box sx={{ mt: 2 }}>
              <MuiTypography variant="body2" color="text.secondary" gutterBottom>
                帳號類型
              </MuiTypography>
              <ToggleButtonGroup
                value={registerRole}
                exclusive
                fullWidth
                size="small"
                onChange={(_e, v) => {
                  if (v) {
                    setRegisterRole(v);
                    setError(null);
                  }
                }}
              >
                <ToggleButton value="admin">一般人員（出貨 / 後台）</ToggleButton>
                <ToggleButton value="counter">門市人員（NOKE）</ToggleButton>
              </ToggleButtonGroup>
              <MuiTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {registerRole === 'counter'
                  ? '只能看到 NOKE 專櫃與 NOKE 收銀，其餘後台一律無法進入。'
                  : '完整後台權限：出貨、訂單、商品、庫存等。'}
              </MuiTypography>
            </Box>
            <TextField
              type="password"
              label={registerRole === 'counter' ? '門市人員註冊碼' : '一般人員註冊碼'}
              fullWidth
              value={secretCode}
              onChange={(e) => setSecretCode(e.target.value)}
              margin="normal"
              required
              helperText={
                registerRole === 'counter'
                  ? '兩種帳號的註冊碼不同，請輸入門市專用的註冊碼'
                  : '兩種帳號的註冊碼不同，請輸入後台人員的註冊碼'
              }
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Register'}
            </Button>
          </form>
        </TabPanel>
      </Paper>
    </Box>
  );
};